import { useSyncExternalStore } from 'react';
import { useMotionPreference } from '../motion/motion-preference/motion-preference-context';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const canMatchMedia = (): boolean => typeof window !== 'undefined' && typeof window.matchMedia === 'function';

const subscribe = (onStoreChange: () => void): (() => void) => {
  if (!canMatchMedia()) return () => undefined;

  const query = window.matchMedia(REDUCED_MOTION_QUERY);
  query.addEventListener('change', onStoreChange);
  return () => query.removeEventListener('change', onStoreChange);
};

const getSnapshot = (): boolean => (canMatchMedia() ? window.matchMedia(REDUCED_MOTION_QUERY).matches : false);

const getServerSnapshot = (): boolean => false;

/**
 * Whether to reduce motion: the player's choice from the nearest `WordsHappenMotionProvider` (On or Off), or, for
 * Follow device and without a provider, the device's `prefers-reduced-motion` setting.
 *
 * Use this for motion that CSS cannot reach — JS-driven animations, or nodes portalled outside the subtree a CSS rule
 * is scoped to. Prefer the `reduced` mixin (`@words-happen/ui-components/reduced-motion.scss`) whenever the animation
 * is expressible in CSS.
 *
 * Subscribes to the `MediaQueryList` rather than reading once, so toggling the OS setting mid-session takes effect
 * immediately. SSR-safe: no `window` access during module evaluation, and the server snapshot is `false` (motion
 * allowed).
 */
export const useReducedMotion = (): boolean => {
  const preference = useMotionPreference();
  const deviceReduces = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  return preference === 'system' ? deviceReduces : preference === 'on';
};
